import type {
  AlertaProduccion,
  AnalisisGeneralMaquinaria,
  AnalisisUnidadesProduccion,
  CeldaAvanceMatriz,
  CeldaAvancePartida,
  CodigoPartida,
  Costo,
  CostoMensual,
  CostoPorCategoria,
  Ejemplo,
  KpiPartida,
  MensualObra,
  ObraDetalle,
  PeriodoSemanal,
  PresupuestoTarea,
  ProductividadObra,
  RegistroGastoMaquina,
  RegistroUsoMaquinaria,
  RespuestaApi,
  RespuestaPaginada,
  ResumenAreaCosteo,
  ResumenKpiObra,
  ResumenUtilizacionMaquina,
  UnidadProduccion,
  ViviendaCosto,
  ViviendaCuadrillas,
  ViviendaGerencia,
} from '@adelante/shared';
import { type FiltrosGlobales, filtrosToQueryString } from './FiltrosGlobalesContext';
import { modoDev, msalInstance, obtenerIniciales, scopesApi, usuarioDev } from './auth';

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Selección de período específica de Maquinaria (independiente del selector
 * global de Períodos) — ver clausulaPeriodoMaquinaria en la API. Mismo shape
 * que emite <FiltroPeriodos onChange>.
 */
export interface PeriodoMaqSel {
  aniosSel: number[];
  mesesSel: string[];
  diasSel: string[];
}

function aplicarPeriodoMaq(params: URLSearchParams, periodo: PeriodoMaqSel | undefined): void {
  if (!periodo) return;
  if (periodo.aniosSel.length) params.set('maqAniosSel', periodo.aniosSel.join(','));
  if (periodo.mesesSel.length) params.set('maqMesesSel', periodo.mesesSel.join(','));
  if (periodo.diasSel.length) params.set('maqDiasSel', periodo.diasSel.join(','));
}

/** Partida del catálogo para el selector jerárquico de Costos/m². */
export interface PartidaCatalogo {
  taskNo: string;
  tareaDisplay: string | null;
}

/** Selección de partida (local al reporte Costos/m²): jerárquica Tarea Padre → Partida. */
export interface PartidaSel {
  tareaPadre?: string;
  partida?: string;
}

/** Fila (obra × partida) de la Matriz Costo/m² × Partida (viviendas, tareas padre 1-4). */
export interface MatrizCostoM2Fila {
  worksNo: string;
  displayName: string | null;
  bloque: string;
  subBloque: string | null;
  areaM2Total: number;
  m2Construidos: number;
  taskNo: string;
  tareaDisplay: string | null;
  tareaPadre: string;
  tareaPadreDisplay: string | null;
  costoDir: number;
  costoMO: number;
}

/**
 * Obtiene un access token para llamar a la API Adelante.
 * - En modo dev: devuelve `null` (el backend skipea validación con AUTH_DEV_MODE=true).
 * - En modo real: pide token silencioso a MSAL para el scope custom de la API.
 */
async function obtenerToken(): Promise<string | null> {
  if (modoDev) return null;

  if (!msalInstance) {
    throw new Error('MSAL no está inicializado');
  }

  const cuentas = msalInstance.getAllAccounts();
  const cuenta = cuentas[0];
  if (!cuenta) {
    throw new Error('Sin sesión activa');
  }

  const respuesta = await msalInstance.acquireTokenSilent({
    scopes: scopesApi,
    account: cuenta,
  });

  return respuesta.accessToken;
}

/**
 * Wrapper genérico sobre `fetch` que añade auth, content-type y maneja errores
 * HTTP a alto nivel. Las funciones del cliente lo usan internamente.
 */
async function pedir<T>(ruta: string, opciones?: RequestInit): Promise<T> {
  const token = await obtenerToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((opciones?.headers ?? {}) as Record<string, string>),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const respuesta = await fetch(`${API_URL}${ruta}`, { ...opciones, headers });

  if (!respuesta.ok) {
    let detalle = respuesta.statusText;
    try {
      const cuerpo = (await respuesta.json()) as { error?: { mensaje?: string } | null };
      if (cuerpo?.error?.mensaje) detalle = cuerpo.error.mensaje;
    } catch {
      // cuerpo no JSON: nos quedamos con statusText
    }
    throw new Error(`HTTP ${respuesta.status}: ${detalle}`);
  }

  return (await respuesta.json()) as T;
}

/**
 * Cliente API tipado. Cada agrupación corresponde a un módulo del backend.
 * Para agregar un endpoint nuevo: extender este objeto y reutilizar `pedir<T>`.
 */
export const cliente = {
  ejemplo: {
    listar: () => pedir<RespuestaApi<Ejemplo[]>>('/api/ejemplo'),
  },
  costos: {
    detallados: (filtros: FiltrosGlobales, pagina = 1, tamanioPagina = 50) => {
      const params = new URLSearchParams(filtrosToQueryString(filtros));
      params.set('pagina', String(pagina));
      params.set('tamanioPagina', String(tamanioPagina));
      return pedir<RespuestaApi<RespuestaPaginada<Costo>>>(
        `/api/costos-detallados?${params.toString()}`,
      );
    },
    mensuales: (filtros: FiltrosGlobales) => {
      const qs = filtrosToQueryString(filtros);
      return pedir<RespuestaApi<CostoMensual[]>>(`/api/costos-mensuales${qs ? `?${qs}` : ''}`);
    },
  },
  avance: {
    matriz: (filtros: FiltrosGlobales, granularidad?: 'mes' | 'semana', semanasSel?: string[]) => {
      const params = new URLSearchParams(filtrosToQueryString(filtros));
      if (granularidad === 'semana') params.set('granularidad', 'semana');
      if (semanasSel && semanasSel.length > 0) params.set('semanasSel', semanasSel.join(','));
      const qs = params.toString();
      return pedir<RespuestaApi<CeldaAvanceMatriz[]>>(`/api/avance-matriz${qs ? `?${qs}` : ''}`);
    },
    kpisObras: (filtros: FiltrosGlobales) => {
      const qs = filtrosToQueryString(filtros);
      return pedir<RespuestaApi<ResumenKpiObra[]>>(`/api/kpis-obras${qs ? `?${qs}` : ''}`);
    },
    /** KPIs recalculados a una fecha de corte (cierre del mes corteAnio/corteMes). */
    kpisObrasCorte: (filtros: FiltrosGlobales, corteAnio: number, corteMes: number) => {
      const params = new URLSearchParams(filtrosToQueryString(filtros));
      params.set('corteAnio', String(corteAnio));
      params.set('corteMes', String(corteMes));
      return pedir<RespuestaApi<ResumenKpiObra[]>>(`/api/kpis-obras-corte?${params.toString()}`);
    },
    kpisPartidas: (worksNo: string) =>
      pedir<RespuestaApi<KpiPartida[]>>(
        `/api/kpis-partidas?worksNo=${encodeURIComponent(worksNo)}`,
      ),
    avancePartidas: (worksNo: string) =>
      pedir<RespuestaApi<CeldaAvancePartida[]>>(
        `/api/avance-partidas?worksNo=${encodeURIComponent(worksNo)}`,
      ),
    partidaDetalle: (worksNo: string, taskNo: string) =>
      pedir<RespuestaApi<CodigoPartida[]>>(
        `/api/partida-detalle?worksNo=${encodeURIComponent(worksNo)}&taskNo=${encodeURIComponent(taskNo)}`,
      ),
    mensualObras: (
      filtros: FiltrosGlobales,
      granularidad?: 'mes' | 'semana',
      semanasSel?: string[],
    ) => {
      const params = new URLSearchParams(filtrosToQueryString(filtros));
      if (granularidad === 'semana') params.set('granularidad', 'semana');
      if (semanasSel && semanasSel.length > 0) params.set('semanasSel', semanasSel.join(','));
      const qs = params.toString();
      return pedir<RespuestaApi<MensualObra[]>>(`/api/mensual-obras${qs ? `?${qs}` : ''}`);
    },
    /** Reporte Vivienda Gerencia: producción, indirecto, costo y M.O. por obra × mes. */
    viviendaGerencia: (filtros: FiltrosGlobales) => {
      const qs = filtrosToQueryString(filtros);
      return pedir<RespuestaApi<ViviendaGerencia[]>>(`/api/vivienda-gerencia${qs ? `?${qs}` : ''}`);
    },
    /** Informe Vivienda Costos: costo directo y mano de obra por obra × partida. */
    viviendaCostos: (filtros: FiltrosGlobales) => {
      const qs = filtrosToQueryString(filtros);
      return pedir<RespuestaApi<ViviendaCosto[]>>(`/api/vivienda-costos${qs ? `?${qs}` : ''}`);
    },
    /** Informe Costo por cuadrilla: M.O. real repartida por horas entre subpartidas y cuadrillas. */
    viviendaCuadrillas: (filtros: FiltrosGlobales) => {
      const qs = filtrosToQueryString(filtros);
      return pedir<RespuestaApi<ViviendaCuadrillas>>(
        `/api/vivienda-cuadrillas${qs ? `?${qs}` : ''}`,
      );
    },
    /** Alerta: obras × mes con producción en BC distinta a las líneas registradas. Sin filtros. */
    alertasProduccion: () => pedir<RespuestaApi<AlertaProduccion[]>>('/api/alertas-produccion'),
    productividad: (
      filtros: FiltrosGlobales,
      corte?: { anio: number; mes: number } | null,
      partidaSel?: PartidaSel,
    ) => {
      const params = new URLSearchParams(filtrosToQueryString(filtros));
      if (corte) {
        params.set('corteAnio', String(corte.anio));
        params.set('corteMes', String(corte.mes));
      }
      if (partidaSel?.tareaPadre) params.set('tareaPadre', partidaSel.tareaPadre);
      if (partidaSel?.partida) params.set('partida', partidaSel.partida);
      const qs = params.toString();
      return pedir<RespuestaApi<ProductividadObra[]>>(`/api/productividad${qs ? `?${qs}` : ''}`);
    },
    costoM2Matriz: (filtros: FiltrosGlobales, corte?: { anio: number; mes: number } | null) => {
      const params = new URLSearchParams(filtrosToQueryString(filtros));
      if (corte) {
        params.set('corteAnio', String(corte.anio));
        params.set('corteMes', String(corte.mes));
      }
      const qs = params.toString();
      return pedir<RespuestaApi<MatrizCostoM2Fila[]>>(`/api/costo-m2-matriz${qs ? `?${qs}` : ''}`);
    },
  },
  partidas: {
    /** Catálogo de partidas (task_no + descripción) para el selector jerárquico. */
    catalogo: (filtros: FiltrosGlobales) => {
      const qs = filtrosToQueryString(filtros);
      return pedir<RespuestaApi<PartidaCatalogo[]>>(`/api/partidas-catalogo${qs ? `?${qs}` : ''}`);
    },
  },
  obra: {
    detalle: (worksNo: string) =>
      pedir<RespuestaApi<ObraDetalle>>(`/api/obra-detalle?worksNo=${encodeURIComponent(worksNo)}`),
  },
  areaCosteo: {
    resumen: (filtros: FiltrosGlobales) => {
      const qs = filtrosToQueryString(filtros);
      return pedir<RespuestaApi<ResumenAreaCosteo[]>>(
        `/api/area-costeo-analisis${qs ? `?${qs}` : ''}`,
      );
    },
    porCategoria: (filtros: FiltrosGlobales) => {
      const qs = filtrosToQueryString(filtros);
      return pedir<RespuestaApi<CostoPorCategoria[]>>(
        `/api/costo-por-categoria${qs ? `?${qs}` : ''}`,
      );
    },
  },
  presupuesto: {
    porPartida: (filtros: FiltrosGlobales) => {
      const qs = filtrosToQueryString(filtros);
      return pedir<RespuestaApi<PresupuestoTarea[]>>(`/api/presupuesto-obras${qs ? `?${qs}` : ''}`);
    },
  },
  periodos: {
    /** Fechas disponibles (YYYY-MM-DD) para el selector jerárquico. Sin filtros. */
    disponibles: () => pedir<RespuestaApi<string[]>>('/api/periodos-disponibles'),
    /** Períodos semanales (anio, mes, semanaIso) con datos. Para el selector
     *  Año › Mes › Semana ISO de Avance/Venta. Sin filtros. */
    semanales: () => pedir<RespuestaApi<PeriodoSemanal[]>>('/api/periodos-semanales'),
  },
  estados: {
    /** Estados de venta disponibles para el filtro global. Sin filtros. */
    venta: () => pedir<RespuestaApi<string[]>>('/api/estados-venta'),
  },
  maquinaria: {
    detalle: (
      filtros: FiltrosGlobales,
      pagina = 1,
      tamanioPagina = 50,
      periodoMaq?: PeriodoMaqSel,
    ) => {
      const params = new URLSearchParams(filtrosToQueryString(filtros));
      params.set('pagina', String(pagina));
      params.set('tamanioPagina', String(tamanioPagina));
      aplicarPeriodoMaq(params, periodoMaq);
      return pedir<RespuestaApi<RespuestaPaginada<RegistroUsoMaquinaria>>>(
        `/api/maquinaria-detalle?${params.toString()}`,
      );
    },
    /** Trae TODAS las filas que matcheen los filtros (sin paginar, tope
     *  20000) — para la tabla con filtro/orden por columna estilo Excel, que
     *  necesita los valores distintos de cada columna en todo el dataset. */
    detalleTodo: (filtros: FiltrosGlobales, periodoMaq?: PeriodoMaqSel) => {
      const params = new URLSearchParams(filtrosToQueryString(filtros));
      params.set('sinPaginar', 'true');
      aplicarPeriodoMaq(params, periodoMaq);
      return pedir<RespuestaApi<RespuestaPaginada<RegistroUsoMaquinaria>>>(
        `/api/maquinaria-detalle?${params.toString()}`,
      );
    },
    utilizacion: (filtros: FiltrosGlobales, periodoMaq?: PeriodoMaqSel) => {
      const params = new URLSearchParams(filtrosToQueryString(filtros));
      aplicarPeriodoMaq(params, periodoMaq);
      const qs = params.toString();
      return pedir<RespuestaApi<ResumenUtilizacionMaquina[]>>(
        `/api/maquinaria-utilizacion${qs ? `?${qs}` : ''}`,
      );
    },
    /** Dashboard de Análisis General: por máquina, serie mensual, desglose de
     *  gasto por categoría. Sin filtros globales, con período propio de
     *  Maquinaria opcional (ver AnalisisGeneralMaquinaria). */
    analisisGeneral: (periodoMaq?: PeriodoMaqSel) => {
      const params = new URLSearchParams();
      aplicarPeriodoMaq(params, periodoMaq);
      const qs = params.toString();
      return pedir<RespuestaApi<AnalisisGeneralMaquinaria>>(
        `/api/maquinaria-analisis-general${qs ? `?${qs}` : ''}`,
      );
    },
    /** Movimientos de uso de una máquina para un canal específico (drill-down
     *  de las columnas Ingreso Partes / Ingreso Recursos) — sin filtros
     *  globales, solo máquina + origen de uso + período propio de Maquinaria. */
    movimientos: (
      maquina: string,
      origenUso: 'PARTE_TRABAJO' | 'ASSEMBLY',
      periodoMaq?: PeriodoMaqSel,
    ) => {
      const params = new URLSearchParams();
      params.set('maquinasSel', maquina);
      params.set('origenUso', origenUso);
      params.set('sinPaginar', 'true');
      aplicarPeriodoMaq(params, periodoMaq);
      return pedir<RespuestaApi<RespuestaPaginada<RegistroUsoMaquinaria>>>(
        `/api/maquinaria-detalle?${params.toString()}`,
      );
    },
    /** Líneas de gasto (bi.fact_costos) de una máquina — drill-down de la
     *  columna Gasto de Análisis General. */
    gastoDetalle: (maquina: string, periodoMaq?: PeriodoMaqSel) => {
      const params = new URLSearchParams();
      params.set('maquina', maquina);
      aplicarPeriodoMaq(params, periodoMaq);
      return pedir<RespuestaApi<RegistroGastoMaquina[]>>(
        `/api/maquinaria-gasto-detalle?${params.toString()}`,
      );
    },
  },
  /** Unidades de Producción: agrupación propia de máquinas (solo warehouse, nunca BC). */
  unidades: {
    analisis: (periodoMaq?: PeriodoMaqSel) => {
      const params = new URLSearchParams();
      aplicarPeriodoMaq(params, periodoMaq);
      const qs = params.toString();
      return pedir<RespuestaApi<AnalisisUnidadesProduccion>>(
        `/api/unidades-produccion${qs ? `?${qs}` : ''}`,
      );
    },
    crear: (cuerpo: { nombre: string; descripcion?: string | null }) =>
      pedir<RespuestaApi<UnidadProduccion>>('/api/unidades-produccion', {
        method: 'POST',
        body: JSON.stringify(cuerpo),
      }),
    editar: (id: number, cuerpo: { nombre: string; descripcion?: string | null }) =>
      pedir<RespuestaApi<UnidadProduccion>>(`/api/unidades-produccion/${id}`, {
        method: 'PUT',
        body: JSON.stringify(cuerpo),
      }),
    borrar: (id: number) =>
      pedir<RespuestaApi<{ borrada: boolean }>>(`/api/unidades-produccion/${id}`, {
        method: 'DELETE',
      }),
    asignar: (codigosMaquina: string[], skUnidadProduccion: number | null) =>
      pedir<RespuestaApi<{ actualizadas: number }>>('/api/maquinaria-asignar-unidad', {
        method: 'PUT',
        body: JSON.stringify({ codigosMaquina, skUnidadProduccion }),
      }),
  },
};

/** Información del usuario activo (modo dev o MSAL real). */
export interface UsuarioActivo {
  name: string;
  username: string;
  iniciales: string;
}

/**
 * Obtiene el usuario activo. En modo dev devuelve `usuarioDev`.
 * En modo MSAL devuelve la primera cuenta o `null` si no hay sesión.
 */
export function obtenerUsuarioActual(): UsuarioActivo | null {
  if (modoDev) return { ...usuarioDev };

  const cuenta = msalInstance?.getAllAccounts()[0];
  if (!cuenta) return null;

  const nombre = cuenta.name ?? cuenta.username;
  return {
    name: nombre,
    username: cuenta.username,
    iniciales: obtenerIniciales(nombre),
  };
}
